// Resource Gantt — 주문 사양 슬라이드 패널
//
// 버킷의 주문 세그먼트를 클릭하면 우측에서 슬라이드되어 나온다.
// - 그 버킷의 주문 목록을 보여주고, 선택된 주문을 강조(캔버스 외곽선과 동기화)
// - 목록에서 다른 주문을 클릭하면 선택이 바뀌어 캔버스 강조도 함께 이동
// - 목록 순서는 캔버스 스택 순서와 눈으로 맞춘다(세로 스택은 첫 세그먼트가 아래이므로 뒤집는다)
// - 아래에 선택 주문의 상세 사양 + 버킷 용량 현황

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import numberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import {
  loadColor,
  OVERLOAD_COLOR,
  CAP_LINE_COLOR,
  LIMIT_LINE_COLOR,
  BUCKET_HEADER_BG,
  HEADER_TEXT_COLOR,
  isOverloaded,
  loadColorRatio,
  ORDER_PANEL_WIDTH_DEFAULT,
  ORDER_PANEL_WIDTH_MIN,
  ORDER_PANEL_WIDTH_MAX,
  BORDER_COLOR,
  SELECTION_ACCENT,
  SELECTION_FILL,
} from "@plannel/pages/master-plan/resource-gantt/ganttConstants";
import { bucketCap, bucketLimitCap, bucketLimitRate, bucketUsed } from "@plannel/pages/master-plan/resource-gantt/ganttScales";

const num = (v) => {
  const n = numberUtil.formatNumber(v, { maximumFractionDigits: 2 });
  return typeof n === "string" ? n : "-";
};

// capacity/부하는 초(seconds) 단위로 저장 → 선택 단위로 변환 (단위 표기는 lang-pack 사용)
const UOM_DIV = { seconds: 1, minutes: 60, hour: 3600 };

const SpecRow = ({ label, value, valueColor }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.75, borderBottom: "1px dashed #eeeeee" }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right", wordBreak: "break-all", color: valueColor }}>
      {value}
    </Typography>
  </Box>
);

// 값 묶음 소제목 — 같은 항목(총능력/여유/초과)을 어떤 기준으로 본 값인지 구분한다.
// accent 색은 캔버스의 대응 기준선 색과 맞춘다(표준=cap 라인, 한도=보조 라인).
const SpecGroupLabel = ({ title, accent }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1.5, mb: 0.25 }}>
    <Box sx={{ width: 3, height: 13, borderRadius: 1, bgcolor: accent, flexShrink: 0 }} />
    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.2 }}>
      {title}
    </Typography>
  </Box>
);

// 버킷 내 주문 한 줄 — 선택된 주문은 강조(캔버스 외곽선과 동기화)
const OrderRow = ({ order, selected, color, onClick, onContextMenu, uomDiv }) => {
  const { t } = useTranslation();
  const rowRef = useRef(null);

  // 방향키로 선택이 옮겨가면 목록 밖으로 나갈 수 있어 보이는 위치로 끌어온다.
  useEffect(() => {
    if (selected && rowRef.current) {
      rowRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [selected]);

  return (
    <Box
      ref={rowRef}
      onClick={onClick}
      onContextMenu={onContextMenu}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.75,
        cursor: "pointer",
        // 선택 표시는 캔버스 세그먼트와 같은 색을 쓴다 — 어느 쪽을 보든 같은 주문임이 읽히게.
        borderLeft: "3px solid",
        borderLeftColor: selected ? SELECTION_ACCENT : "transparent",
        bgcolor: selected ? SELECTION_FILL : "transparent",
        "&:hover": { bgcolor: selected ? SELECTION_FILL : "action.hover" },
      }}
    >
      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: selected ? 700 : 500 }}>
          {/* 분할 주문은 orderNo 가 같아 번호 없이는 구분되지 않는다 — 엔진의 #001 표기와 같은 형태로 붙인다 */}
          {(order.orderNo ?? order.item) + (order.orderSeqNum != null ? ` #${order.orderSeqNum}` : "")}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
          {order.item}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ flexShrink: 0, textAlign: "right" }}>
        {num(order.capacity / uomDiv)}
        {/* capacity 는 setup 을 포함한 값 → 한 줄에 나란히 놓이므로 Setup 을 명시해 내부 몫임을 알린다 */}
        {(order.setupCapacity || 0) > 0 && ` (${t("capaSetup")} ${num((order.setupCapacity || 0) / uomDiv)})`}
      </Typography>
    </Box>
  );
};

// 접기 가능한 섹션 제목 — 클릭 토글 + 배경
const SectionHeader = ({ title, open, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 2,
      py: 0.75,
      cursor: "pointer",
      userSelect: "none",
      bgcolor: "action.hover",
      borderTop: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
      {title}
    </Typography>
    {open ? <ExpandLessIcon fontSize="small" color="action" /> : <ExpandMoreIcon fontSize="small" color="action" />}
  </Box>
);

const OrderSpecPanel = ({
  hit,
  bucketLabels = {},
  colorForItem,
  loadColorFn = loadColor,
  timeUom = "seconds",
  stackDir = "vertical", // 캔버스 스택 방향 — 목록 순서를 캔버스와 맞추는 데 쓴다
  width = ORDER_PANEL_WIDTH_DEFAULT, // 패널 폭(px) — 페이지가 뷰 상태로 보관한다
  onWidthChange, // 스플리터 드래그 결과를 페이지로 올린다
  onSelectOrder,
  onOrderContextMenu, // (order, clientX, clientY) => void — 차트 세그먼트와 같은 컨텍스트 메뉴
  onClose,
}) => {
  const { t } = useTranslation();
  const open = Boolean(hit);
  const [resOpen, setResOpen] = useState(true); // 리소스 정보 접기/펴기
  const [ordersOpen, setOrdersOpen] = useState(false); // 버킷 내 주문 목록 — 기본 접힘
  const [orderOpen, setOrderOpen] = useState(true); // 주문 정보 접기/펴기
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef({ startX: 0, startWidth: 0 });

  const handleResizeStart = (e) => {
    if (!onWidthChange) return; // 폭을 올려받을 곳이 없으면 조절 불가
    e.preventDefault(); // 드래그 중 텍스트 선택 방지
    dragRef.current = { startX: e.clientX, startWidth: width };
    setResizing(true);
  };

  // 드래그는 핸들 밖으로 나가도 이어져야 하므로 window 에 붙인다.
  useEffect(() => {
    if (!resizing) return undefined;

    const handleMove = (e) => {
      // 패널이 오른쪽에 붙어 있어 왼쪽으로 끌면 넓어진다.
      const next = dragRef.current.startWidth + (dragRef.current.startX - e.clientX);
      onWidthChange(Math.min(Math.max(next, ORDER_PANEL_WIDTH_MIN), ORDER_PANEL_WIDTH_MAX));
    };
    const handleUp = () => setResizing(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
    };
  }, [resizing, onWidthChange]);

  // 주문 유형(z_mp_order.order_type) 코드 → 언어팩 라벨. 매핑에 없는 코드는 원본 값을 그대로 보여준다.
  const orderTypeLabel = (code) => {
    if (!code) return "-";
    const langKey = CodeUtil.ganttOrderType[code];
    return langKey ? t(langKey) : code;
  };

  const uomDiv = UOM_DIV[timeUom] || UOM_DIV.minutes;
  const uomAbbr = t(timeUom); // 단위 표기 = lang-pack (초/분/시)
  const cap2 = (sec) => `${num((sec ?? 0) / uomDiv)} ${uomAbbr}`; // 용량값(초) → 단위 변환 + 단위표기

  const orders = useMemo(() => hit?.bucket?.items ?? [], [hit]);
  // 캔버스는 items[0] 을 스택의 시작점에 둔다 — 세로 스택이면 그게 막대 '아래'다.
  // 목록은 위에서 아래로 읽으므로 그대로 두면 차트와 순서가 뒤집혀 보인다.
  const displayOrders = useMemo(
    () => (stackDir === "vertical" ? [...orders].reverse() : orders),
    [orders, stackDir]
  );
  const selectedOrderNo = hit?.item?.orderNo;
  // 목록 행 선택 표시 — 분할 주문은 orderNo 가 같으므로 분할 번호까지 비교해야 조각을 구분한다.
  // 무주문 생산(orderNo null)은 같은 버킷에서 품목이 유일하므로 품목으로 매칭한다(캔버스 강조 기준과 동일).
  const isSelectedRow = (o) =>
    selectedOrderNo != null
      ? o.orderNo === selectedOrderNo && o.orderSeqNum === hit.item?.orderSeqNum
      : o.item === hit?.item?.item;
  const ordersSetupTotal = orders.reduce((s, it) => s + (it.setupCapacity ?? 0), 0); // capacity 에 포함된 setup 몫

  // 버킷 용량 현황 계산
  const cap = bucketCap(hit?.bucket);
  const total = bucketUsed(hit?.bucket);
  const hasCap = cap > 0;
  const utilization = hasCap ? (total / cap) * 100 : 0;
  const remain = Math.max(cap - total, 0);
  // 허용오차(표시 반올림 이하 미세 초과)를 넘겼을 때만 초과로 본다 → 색·초과값이 화면 숫자와 일치.
  const over = isOverloaded(total, cap) ? Math.max(total - cap, 0) : 0;
  // 표준(총능력) 기준 가동률 — 아래 Capacity 그룹의 가동률 행에 쓴다.
  // capacity가 0이면 부하가 곧 초과 → 0%로 오인되지 않게 초과로 표기.
  const utilLabel = hasCap ? `${num(utilization)}%` : total > 0 ? t("overload") : "0%";

  // 사용 한도(usageLimitRate) 기준 값 세트 — 위 표준 기준과 같은 항목을 한도 용량으로 다시 계산한다.
  // 한계 가동률 = 부하 / (총능력 × usageLimitRate) × 100 — Resource Utilization 화면(limitUtilRate)과 같은 식.
  const limitRate = bucketLimitRate(hit?.bucket);
  const limitRateLabel = `${num(limitRate * 100)}%`;
  const limitCap = bucketLimitCap(hit?.bucket);
  const hasLimitCap = limitCap > 0;
  const limitRemain = Math.max(limitCap - total, 0);
  const isLimitOver = isOverloaded(total, limitCap);
  const limitOver = isLimitOver ? Math.max(total - limitCap, 0) : 0;
  const limitUtilLabel = hasLimitCap ? `${num((total / limitCap) * 100)}%` : total > 0 ? t("overload") : "0%";
  // 한도 비율이 100%(미설정)면 표준 기준과 값이 전부 같아 그룹을 나눌 의미가 없다.
  const hasLimitGroup = limitCap !== cap;

  // 상단 칩 = 초과 판정 기준(사용 한도)의 부하율과 색. 캔버스의 부하색·기준선 판정과 같은 기준이라
  // 같은 버킷이 화면 두 곳에서 다른 색으로 보이지 않는다. 한도 미설정이면 총능력 기준과 같은 값이다.
  const chipColor = hasLimitCap ? loadColorFn(loadColorRatio(total, limitCap)) : total > 0 ? OVERLOAD_COLOR : loadColorFn(0);

  // 선택 주문의 Load 중 setup 이 차지하는 비율 — Load(capacity) 안에 setup 이 포함돼 있음을 알리는 표시용.
  const itemLoad = hit?.item?.capacity ?? 0;
  const itemSetup = hit?.item?.setupCapacity ?? 0;
  const setupShare = itemLoad > 0 ? (itemSetup / itemLoad) * 100 : null;

  return (
    // 레이아웃 아이템으로서 폭(0↔width)을 애니메이션 → 옆의 차트가 밀린다(push).
    <Paper
      variant="outlined"
      square
      sx={{
        flexShrink: 0,
        height: "100%",
        width: open ? width : 0,
        overflow: "hidden",
        position: "relative", // 좌측 스플리터 핸들 기준
        // 드래그 중에는 트랜지션을 끈다 — 켜두면 폭이 마우스를 따라오지 못해 끈적하게 밀린다.
        transition: resizing ? "none" : "width .25s ease",
        borderTop: 0,
        borderBottom: 0,
        borderRight: 0,
        borderLeftWidth: open ? 1 : 0,
        borderColor: BORDER_COLOR, // 간트 캔버스·그리드 테두리와 같은 색(--ag-border-color)
      }}
    >
      {/* 좌측 스플리터 — 왼쪽으로 끌면 넓어진다. 폭은 PANEL_WIDTH_MIN~MAX 로 제한. */}
      {open && (
        <Box
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 6,
            zIndex: 2,
            cursor: "col-resize",
            bgcolor: resizing ? "primary.main" : "transparent",
            opacity: resizing ? 0.4 : 1,
            "&:hover": { bgcolor: "primary.main", opacity: 0.25 },
          }}
        />
      )}
      {/* 폭이 줄어드는 동안 내용이 찌그러지지 않도록 고정폭 컨테이너 */}
      <Box sx={{ width, height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            bgcolor: BUCKET_HEADER_BG,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: HEADER_TEXT_COLOR }}>
            {t("orderSpec")}
          </Typography>
          <IconButton size="small" onClick={onClose} aria-label={t("close")} sx={{ color: HEADER_TEXT_COLOR }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        {hit && (
          <Box sx={{ overflowY: "auto" }}>
            {/* 1) 리소스 정보 */}
            <SectionHeader title={t("resource")} open={resOpen} onToggle={() => setResOpen((v) => !v)} />
            <Collapse in={resOpen} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, py: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {hit.resource.name ?? hit.resource.id}
                  </Typography>
                  {/* 칩은 초과 판정 기준(한도)의 부하율 — 그룹 안 두 가동률 중 판정에 쓰는 쪽 */}
                  <Chip size="small" label={limitUtilLabel} sx={{ color: "#fff", bgcolor: chipColor }} />
                </Box>
                <SpecRow label={t("location")} value={hit.resource.group ?? "-"} />
                <SpecRow label={t("calendar")} value={hit.resource.calendar ?? "-"} />
                <SpecRow label={t("period")} value={bucketLabels[hit.bucket.key] ?? hit.bucket.key} />
                {/* master efficiency = z_resource_capacity.efficiency (비율 → %). 값 없으면 기본 1(=100%). 상단 칩은 결과(부하율). */}
                <SpecRow
                  label={t("efficiency")}
                  value={`${num((hit.bucket.efficiency != null ? hit.bucket.efficiency : 1) * 100)}%`}
                />
                {/* 표준 기준 — 계획 엔진이 한계로 쓰는 총능력(z_resource_util.capa_total) 기준 값 */}
                <SpecGroupLabel title={t("capacity")} accent={CAP_LINE_COLOR} />
                <SpecRow label={t("totalCapacity")} value={cap2(cap)} />
                <SpecRow label={t("usedCapacity")} value={cap2(total)} />
                <SpecRow label={t("remainCapacity")} value={cap2(remain)} />
                <SpecRow label={t("overload")} value={cap2(over)} valueColor={over > 0 ? OVERLOAD_COLOR : undefined} />
                {/* 가동률 = 부하 / 총능력 × 100. 한도 그룹의 한계 가동률과 같은 자리(그룹 마지막)에 둔다.
                    라벨 키는 Resource Utilization 화면과 동일한 쌍(capaUtil / limitUtilRate)을 쓴다. */}
                <SpecRow label={t("capaUtil")} value={utilLabel} valueColor={over > 0 ? OVERLOAD_COLOR : undefined} />
                {/* 사용 한도 기준 — 초과 판정에 쓰는 기준. 두 그룹을 같은 항목 순서로 읽을 수 있게
                    부하(사용)도 같이 둔다(값은 기준과 무관하게 동일).
                    비율 100%면 표준과 동일해 그룹 없이 비율 한 줄만 보여준다. */}
                {hasLimitGroup ? (
                  <>
                    <SpecGroupLabel title={`${t("usageLimitRate")} ${limitRateLabel}`} accent={LIMIT_LINE_COLOR} />
                    <SpecRow label={t("totalCapacity")} value={cap2(limitCap)} />
                    <SpecRow label={t("usedCapacity")} value={cap2(total)} />
                    <SpecRow label={t("remainCapacity")} value={cap2(limitRemain)} />
                    <SpecRow label={t("overload")} value={cap2(limitOver)} valueColor={limitOver > 0 ? OVERLOAD_COLOR : undefined} />
                    <SpecRow
                      label={t("limitUtilRate")}
                      value={limitUtilLabel}
                      valueColor={isLimitOver ? OVERLOAD_COLOR : undefined}
                    />
                  </>
                ) : (
                  <SpecRow label={t("usageLimitRate")} value={limitRateLabel} />
                )}
              </Box>
            </Collapse>

            {/* 2) 버킷 내 주문 목록 — 선택 주문 강조 */}
            <SectionHeader title={`${t("bucketOrders")} (${orders.length})`} open={ordersOpen} onToggle={() => setOrdersOpen((v) => !v)} />
            <Collapse in={ordersOpen} timeout="auto" unmountOnExit>
              {/* selected: orderNo 는 무주문 생산(order_cd null)이면 null → null끼리 매칭되어 전부 강조되는 것을
                  막는다. 이 경우 같은 버킷 안에서는 품목이 유일하므로 품목으로 매칭한다(캔버스 강조 기준과 동일). */}
              {displayOrders.map((o) => (
                <OrderRow
                  key={`${o.orderNo ?? "-"}_${o.item ?? "-"}_${o.orderSeqNum ?? "-"}`}
                  order={o}
                  selected={isSelectedRow(o)}
                  color={colorForItem ? colorForItem(o.item) : "transparent"}
                  uomDiv={uomDiv}
                  onClick={() => onSelectOrder && onSelectOrder(o)}
                  onContextMenu={(e) => {
                    if (!onOrderContextMenu) return;
                    e.preventDefault(); // 브라우저 기본 메뉴 대신 차트와 같은 메뉴를 띄운다
                    onSelectOrder && onSelectOrder(o); // 메뉴 동작 대상과 선택 상태를 일치시킨다
                    onOrderContextMenu(o, e.clientX, e.clientY);
                  }}
                />
              ))}
              <Box sx={{ px: 1.5, py: 1, borderTop: "1px dashed", borderColor: "divider", bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "right" }}>
                  {`${t("load")} ${t("total")}: ${cap2(total)}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "right" }}>
                  {`${t("capaSetup")} ${t("total")}: ${cap2(ordersSetupTotal)}`}
                </Typography>
              </Box>
            </Collapse>

            {/* 3) 선택 주문 정보 */}
            <SectionHeader title={t("orderInfo")} open={orderOpen} onToggle={() => setOrderOpen((v) => !v)} />
            <Collapse in={orderOpen} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, py: 1 }}>
                <SpecRow label={t("orderNo")} value={hit.item.orderNo ?? "-"} />
                {/* 분할 주문 번호(z_mp_operation_plan.order_seq_num) — 조각마다 setup 이 따로 붙는다 */}
                <SpecRow label={t("orderSeqNum")} value={hit.item.orderSeqNum ?? "-"} />
                <SpecRow label={t("orderType")} value={orderTypeLabel(hit.item.orderType)} />
                <SpecRow label={t("customer")} value={hit.item.customer ?? "-"} />
                <SpecRow label={t("item")} value={hit.item.item} />
                <SpecRow label={t("qty")} value={num(hit.item.qty)} />
                <SpecRow label={t("dueDate")} value={hit.item.dueDate ?? "-"} />
                <SpecRow label={t("prodDate")} value={hit.item.prodDate ?? "-"} />
                {/* Load = 응답 capacity 그대로. capacity 는 setup 을 포함한 값이므로
                    Setup Load 는 '↳' 들여쓰기 + Load 대비 비율로 "이만큼 포함"을 알린다(값 자체는 응답 그대로). */}
                <SpecRow label={t("load")} value={cap2(hit.item.capacity)} />
                {(hit.item.setupCapacity || 0) > 0 && (
                  <SpecRow
                    label={`↳ ${t("capaSetup")}`}
                    value={`${cap2(hit.item.setupCapacity)}${setupShare != null ? ` (${num(setupShare)}%)` : ""}`}
                  />
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default OrderSpecPanel;
